import React, { useState, useEffect, useRef } from 'react';
import { useStytchB2BClient, useStytchMemberSession } from '@stytch/react/b2b';
import { ChevronDown } from 'lucide-react';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { environmentLabel } from '../utils/environmentLabel';

/**
 * EnvironmentSelector - Stacked label/value card with custom dropdown
 * Mock2-style: small uppercase label on top, bold value below, dropdown menu on click
 */
const EnvironmentSelector = () => {
  const { selectedEnvironment, setEnvironment, environments, fetchEnvironments } = useEnvironment();
  const stytchClient = useStytchB2BClient();
  const { session } = useStytchMemberSession();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  // Fallback: fetch if cache is empty (handles deep links). Keyed on the
  // stable session id — the session OBJECT changes identity on every Stytch
  // heartbeat and would re-trigger this every ~4 minutes.
  const sessionId = session?.member_session_id;
  useEffect(() => {
    if (environments.length === 0 && stytchClient?.session?.getTokens()?.session_jwt && session) {
      setLoading(true);
      fetchEnvironments(stytchClient, session).finally(() => setLoading(false));
    }
  }, [environments.length, stytchClient, sessionId, fetchEnvironments]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  if (loading) {
    return (
      <div className="relative flex flex-col items-start">
        <span className="block text-[10px] text-text-secondary uppercase tracking-wide mb-1 pl-3">Env</span>
        <div className="px-3 py-2 rounded-xl bg-background-tertiary min-w-[140px]">
          <span className="text-sm text-text-muted leading-snug">Loading...</span>
        </div>
      </div>
    );
  }

  if (environments.length === 0) return null;

  const currentEnv = environments.find(e => e.name === selectedEnvironment);
  const displayName = currentEnv ? environmentLabel(currentEnv) : selectedEnvironment || 'Select';

  return (
    <div className="relative flex flex-col items-start" ref={ref}>
      <span className="block text-[10px] text-text-secondary uppercase tracking-wide mb-1 pl-3">Env</span>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-tertiary min-w-[140px] cursor-pointer transition-colors hover:bg-background-quaternary"
      >
        <span className="text-sm font-semibold text-text-primary leading-snug">
          {displayName}
        </span>
        <ChevronDown size={12} className={`text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-[calc(100%+6px)] w-full min-w-[160px] rounded-xl p-1.5 z-50"
          style={{
            backgroundColor: 'var(--color-panel-secondary)',
            border: '1px solid var(--color-border-strong)',
            boxShadow: 'var(--shadow-dropdown)',
          }}
        >
          {environments.map((env) => (
            <button
              type="button"
              key={env.name}
              onClick={() => { setEnvironment(env.name); setIsOpen(false); }}
              className={`w-full text-left px-2.5 py-2 rounded-lg text-sm transition-colors ${
                selectedEnvironment === env.name
                  ? 'text-accent-primary-base font-medium'
                  : 'text-text-primary hover:bg-background-hover'
              }`}
            >
              {environmentLabel(env)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnvironmentSelector;
